import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactNode } from 'react';
import { useRef } from 'react';

import type { TaskDto } from '@botguide/shared';
import { formatWeekLabel } from '@botguide/shared';

import {
  BlockedBadge,
  BlockedSurface,
  Card,
  Icon,
  PersonChip,
  PointsPill,
  StageBadge,
  laneColour,
  stageColour,
} from '@/components';
import { cn } from '@/lib/cn';

/** How far the pointer may travel before a click counts as a drag, in pixels. */
const CLICK_SLOP = 6;

interface TaskCardProps {
  task: TaskDto;
  /** The droppable this card currently lives in. Read back on drop. */
  groupId: string;
  /** False for a viewer who may not move work - the card is then inert. */
  canMove: boolean;
  onOpen: (task: TaskDto) => void;
}

/**
 * One task, as a draggable card.
 *
 * ## Why there is a grip as well as a draggable body
 *
 * The body carries the pointer listeners so a mouse can drag from anywhere,
 * and the grip is the *keyboard* entry point: it is the focusable element
 * dnd-kit's `attributes` wire up, so Tab lands on it, Space picks the card up
 * and the arrow keys move it. Putting the keyboard activator on the card body
 * instead would mean Enter both opened the task and started a drag, and one of
 * the two would have to lose.
 *
 * The title is its own button, so the dialog is reachable by keyboard without
 * going anywhere near the drag machinery.
 */
export function TaskCard({ task, groupId, canMove, onOpen }: TaskCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { type: 'task', groupId },
    disabled: !canMove,
  });

  // Where the pointer went down, so a drag that ends over the card is not also
  // read as a click. dnd-kit does not swallow the click for us.
  const pressedAt = useRef<{ x: number; y: number } | null>(null);

  const style: CSSProperties = {
    // Translate only: a scaling transform would make the card jitter against
    // the others as the list reflows around it.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn('list-none touch-manipulation', canMove && 'cursor-grab active:cursor-grabbing')}
      {...listeners}
      onPointerDownCapture={(event) => {
        pressedAt.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={(event) => {
        if (event.target instanceof Element && event.target.closest('button')) return;
        const from = pressedAt.current;
        if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > CLICK_SLOP) return;
        onOpen(task);
      }}
    >
      <TaskCardFace
        task={task}
        onOpenTitle={() => onOpen(task)}
        handle={
          canMove ? (
            <button
              type="button"
              {...attributes}
              {...listeners}
              aria-label={`Move ${task.title}`}
              className="-mr-1 -mt-1 grid size-6 shrink-0 cursor-grab place-items-center rounded-sm text-subtle transition-colors hover:bg-sunken hover:text-ink active:cursor-grabbing"
            >
              <Icon name="menu" size={13} />
            </button>
          ) : null
        }
      />
    </li>
  );
}

/**
 * The card itself, with no drag behaviour attached.
 *
 * Exported so the drag overlay renders the exact same thing that is being
 * dragged - a preview that differs from the card is how a board starts feeling
 * imprecise.
 */
export function TaskCardFace({
  task,
  handle,
  onOpenTitle,
  lifted = false,
}: {
  task: TaskDto;
  handle?: ReactNode;
  onOpenTitle?: () => void;
  /** Renders the floating state used inside the drag overlay. */
  lifted?: boolean;
}) {
  const title = (
    <span className="line-clamp-2 text-[0.8125rem] font-medium leading-snug">{task.title}</span>
  );

  return (
    <BlockedSurface blocked={task.isBlocked}>
      <Card
        // The rail names where the card is: its stage while one applies, its
        // lane otherwise. Both are tokens keyed by the shared enum value.
        accent={task.stage ? stageColour(task.stage) : laneColour(task.lane)}
        padding="sm"
        className={cn('flex flex-col gap-2', lifted && 'rotate-[0.6deg] shadow-float')}
      >
        <div className="flex items-start gap-1.5">
          {onOpenTitle ? (
            <button
              type="button"
              onClick={onOpenTitle}
              className="min-w-0 flex-1 text-left hover:text-accent-ink"
            >
              {title}
            </button>
          ) : (
            <span className="min-w-0 flex-1">{title}</span>
          )}
          {handle}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <PointsPill points={task.storyPoints} size="sm" />
          {task.stage ? <StageBadge stage={task.stage} size="sm" /> : null}
          <span className="ml-auto whitespace-nowrap font-mono text-[0.6875rem] text-subtle tnum">
            {task.plannedWeek ? formatWeekLabel(task.plannedWeek) : 'No week'}
          </span>
        </div>

        <PersonChip name={task.assigneeName} id={task.assigneeId} size="sm" />

        {task.isBlocked ? <BlockedBadge reason={task.blockedReason} className="self-start" /> : null}
      </Card>
    </BlockedSurface>
  );
}
